import { useEffect, useState } from 'react';
import { CircularProgress, FormControl, FormControlLabel, Grid, Select, Switch, Typography } from '@material-ui/core';
import { TextField } from '@mui/material';
import { useGetActiveProcedure } from 'circulator/hooks/procedure/useGetActiveProcedure';
import { useCanUseCuts } from 'client/hooks/mobile/useCanUseCuts';
import * as PartnerServices from 'common/services/partnerServices';
import { StartMobileProcedureButton } from 'mobile/components/StartMobileProcedureButton';
import { BackgroundWrapper } from 'mobile/components/UI/BackgroundWrapper';
import { useListFacilitiesForCuts } from 'mobile/hooks/facility/useListFacilitiesForCuts';
import useMobileLayouts from 'mobile/hooks/useMobileLayouts';
import useMobileClasses from 'mobile/hooks/useMobileStyles';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import CutsLogo from '../../img/cuts-logo.png';

export const StartShotsPage = () => {
    const mobileClasses = useMobileClasses();
    const layouts = useMobileLayouts();
    const navigate = useNavigate();
    const { procedure, isLoading: isProcedureLoading } = useGetActiveProcedure();
    const { isLoading: isCutsLoading, userDisplayName } = useCanUseCuts(true);
    const { facilities, isLoading: isFacilitiesLoading } = useListFacilitiesForCuts();
    const [sessionType, setSessionType] = useState('Public');
    const [facilityId, setFacilityId] = useState(0);
    const [patientInitials, setPatientInitials] = useState('NA');
    const [title, setTitle] = useState('');
    const [notify, setNotify] = useState(true);
    const [company, setCompany] = useState('');
    const [companyOptions, setCompanyOptions] = useState<{ value: string; category: string }[]>([]);

    const { isLoading: isLoadingOptions } = useQuery(['partners'], PartnerServices.getAllPartnersService, {
        enabled: true,
        onSuccess: success => {
            const allOptions = success.data || [];

            setCompanyOptions(allOptions.filter((option: { category: string }) => option.category === 'COMPANY'));
        },
    });

    const isLoading = isCutsLoading || isFacilitiesLoading || isProcedureLoading || isLoadingOptions;

    useEffect(() => {
        if (procedure) {
            navigate('/mobile/shot');
        }
    }, [procedure, navigate]);

    useEffect(() => {
        //when facilities are loaded, set the first facility as the default
        if (facilities && facilities.length > 0 && facilityId === 0) {
            setFacilityId(facilities[0].id);
        }
    }, [facilities, isFacilitiesLoading, facilityId]);

    const onSessionTypeSwitch = () => {
        setSessionType(sessionType === 'Private' ? 'Public' : 'Private');
    };

    return (
        <BackgroundWrapper headerImage={CutsLogo} backButtonVisible={true} backButtonHref="/mobile/create-or-archives">
            {isLoading && <CircularProgress size={20} />}
            {!isLoading && (
                <div className={layouts.verticalContainer}>
                    <Typography className={mobileClasses.text}>Adjust the options below or select Start Session.</Typography>
                    <div className={`${layouts.dynamicContainer} ${layouts.padded}`} style={{ flexGrow: 3 }}>
                        <div className={`${mobileClasses.dropdown} ${layouts.verticalContainer} ${layouts.padded}`}>
                            <div>
                                <Typography className={mobileClasses.text} style={{ textAlign: 'center' }}>
                                    Facility
                                </Typography>
                            </div>
                            <FormControl fullWidth variant="filled">
                                <Select
                                    native
                                    className={`${mobileClasses.dropdown}`}
                                    onInput={ev => setFacilityId(() => Number((ev.target as HTMLSelectElement).value))}
                                >
                                    {facilities?.map(facility => (
                                        <option key={facility.id} value={facility.id}>
                                            {facility.name}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div className={`${mobileClasses.dropdown} ${layouts.verticalContainer} ${layouts.padded}`}>
                            <div>
                                <Typography className={mobileClasses.text} style={{ textAlign: 'center' }}>
                                    Description
                                </Typography>
                            </div>
                            <FormControl fullWidth>
                                <TextField className={mobileClasses.input} variant="filled" onChange={e => setTitle(e.target.value)} />
                            </FormControl>
                        </div>
                        <div className={`${layouts.verticalContainer} ${mobileClasses.dropdown} ${layouts.padded}`}>
                            <div>
                                <Typography className={mobileClasses.text} style={{ textAlign: 'center' }}>
                                    ID
                                </Typography>
                            </div>
                            <TextField
                                className={mobileClasses.input}
                                variant="filled"
                                value={patientInitials}
                                onChange={e => setPatientInitials(e.target.value)}
                            />
                            {false && (
                                <FormControlLabel
                                    control={<Switch color="secondary" onChange={onSessionTypeSwitch} checked={sessionType === 'Public'} />}
                                    label={sessionType}
                                    labelPlacement="top"
                                />
                            )}
                        </div>
                        <div className={`${mobileClasses.dropdown} ${layouts.verticalContainer} ${layouts.padded}`}>
                            <div>
                                <Typography className={mobileClasses.text} style={{ textAlign: 'center' }}>
                                    Company
                                </Typography>
                            </div>
                            <FormControl fullWidth variant="filled">
                                <Select
                                    native
                                    className={`${mobileClasses.dropdown}`}
                                    onInput={ev => setCompany(() => (ev.target as HTMLSelectElement).value)}
                                >
                                    <option key="" value="">
                                        - None -
                                    </option>
                                    {companyOptions?.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.value}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                        </div>
                        <div className={`${layouts.verticalContainer} ${layouts.padded}`}>
                            <FormControlLabel
                                control={
                                    <Grid component="label" container spacing={0}>
                                        <Grid item>Off</Grid>
                                        <Grid item>
                                            <Switch
                                                checked={notify}
                                                onChange={() => setNotify(!notify)}
                                                color="primary"
                                                inputProps={{ 'aria-label': 'controlled' }}
                                            />
                                        </Grid>
                                        <Grid item>On</Grid>
                                    </Grid>
                                }
                                label="Notify&nbsp;users"
                                labelPlacement="top"
                            />
                        </div>
                    </div>
                    <div className={`${layouts.dynamicContainer} `}>
                        <div className={layouts.verticalContainer}>
                            <Typography style={{ textAlign: 'center', fontSize: '0.8em' }}>
                                By choosing Start Session, I confirm that I have received the required permission from all necessary parties to make
                                this recording.
                            </Typography>
                        </div>
                        <div className={layouts.verticalContainer}>
                            <StartMobileProcedureButton
                                isPublic={sessionType === 'Public'}
                                facilityId={facilityId}
                                patientInitials={patientInitials}
                                title={title}
                                drName={userDisplayName}
                                notify={notify}
                                company={company}
                            />
                        </div>
                    </div>
                </div>
            )}
        </BackgroundWrapper>
    );
};
